import { forwardRef } from 'react'
import type { ReactNode } from 'react'
import { Button } from '../Button/Button'
import './CookieBanner.css'

export interface CookieBannerProps {
  onAcceptAll: () => void
  onRejectAll: () => void
  onCustomize: () => void
  title?: ReactNode
  children: ReactNode
  acceptLabel?: string
  rejectLabel?: string
  customizeLabel?: string
  ariaLabel?: string
}

/**
 * Bandeau de consentement, ancré en bas de l'écran et volontairement non
 * bloquant : il ne couvre pas la page et n'empêche pas de la parcourir, la
 * CNIL interdisant de conditionner l'accès au site à un choix.
 *
 * Deux conséquences visibles dans ce composant :
 *
 * - « Tout refuser » et « Tout accepter » sont deux `Button` de la *même*
 *   variante, côte à côte dans le même conteneur : même fond, même taille,
 *   même graisse. Refuser doit coûter exactement un clic, comme accepter.
 *   Changer la variante de l'un sans l'autre rendrait le bandeau non conforme.
 * - Il n'y a **pas** de croix de fermeture. Fermer un bandeau sans choisir ne
 *   vaut ni acceptation ni refus : la croix ne pourrait que masquer le bandeau
 *   en laissant le visiteur sans choix enregistré — donc le bandeau
 *   réapparaîtrait. Les trois boutons sont les seules sorties.
 *
 * La `ref` est posée sur la région, qui porte `tabIndex={-1}` : elle permet à
 * l'appelant de rendre le focus au bandeau lorsqu'il réapparaît après la
 * fermeture du panneau de réglages, le bouton qui l'avait ouvert ayant alors
 * disparu avec le bandeau démonté.
 */
export const CookieBanner = forwardRef<HTMLElement, CookieBannerProps>(function CookieBanner(
  {
    onAcceptAll,
    onRejectAll,
    onCustomize,
    title = 'Votre vie privée',
    children,
    acceptLabel = 'Tout accepter',
    rejectLabel = 'Tout refuser',
    customizeLabel = 'Personnaliser',
    ariaLabel = 'Consentement aux cookies',
  },
  ref,
) {
  return (
    <section
      className="azoya-cookie-banner"
      role="region"
      aria-label={ariaLabel}
      tabIndex={-1}
      ref={ref}
    >
      <div className="azoya-cookie-banner-body">
        {title && <h2 className="azoya-cookie-banner-title">{title}</h2>}
        <p className="azoya-cookie-banner-text">{children}</p>
      </div>
      <div className="azoya-cookie-banner-actions">
        <Button
          variant="light"
          className="azoya-cookie-banner-secondary"
          onClick={onCustomize}
        >
          {customizeLabel}
        </Button>
        <Button onClick={onRejectAll}>{rejectLabel}</Button>
        <Button onClick={onAcceptAll}>{acceptLabel}</Button>
      </div>
    </section>
  )
})
